import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Link } from '@mui/material';

import PlanContainer from '../../components/PlanContainer';
import CtaButton from '../../components/CtaButton';
import PlanPrice from '../../components/PlanPrice';
import PlanDetailsContainer from '../../components/PlanDetailsContainer';
import CheckMarkList from '../../components/CheckMarkList';
import PlanExampleContainer from '../../components/PlanExampleContainer';
import PlanDescription from '../../components/PlanDescription';
import PlanFeaturesList from '../../components/PlanFeaturesList';
import usePriceConfig from '../../../shared/features/priceConfig/hooks/usePriceConfig';
import AmplitudeAnalytics from '../../../shared/services/AmplitudeAnalytics';
import { Plan } from '../../../shared/helpers/constants/plans';
import rootStore from '../rootStore/RootStore';

const EnterprisePlan = observer(() => {
  const { t } = useTranslation();
  const { isLoaded: priceConfigLoaded } = usePriceConfig(rootStore);

  const handleCtaClick = () => {
    AmplitudeAnalytics.trackPlanSelect(Plan.enterprise);
  };

  return (
    <PlanContainer showPlaceholder={!priceConfigLoaded}>
      <PlanDescription>
        <Fragment key="title">{t('plans.enterprise.title')}</Fragment>
        <Fragment key="description">
          {t('plans.enterprise.description')}
        </Fragment>
        <PlanPrice
          key="price"
          title={t('plans.enterprise.discuss-price')}
          titleAlign="center"
        />
      </PlanDescription>
      <CtaButton
        variant="outlined"
        label={t('plan-select.action-button.talk-with-team')}
        to={import.meta.env.VITE_DEMO_FORM_URL}
        onClick={handleCtaClick}
      />
      <PlanDetailsContainer>
        <PlanFeaturesList
          title={t('plan-select.details.features')}
          items={[
            t('plans.enterprise.details.custom-pricing'),
            t('plans.enterprise.details.scalability'),
            t('plans.enterprise.details.dedicated-support'),
            t('plans.enterprise.details.custom-onboarding'),
            t('plans.enterprise.details.api-access'),
            t('plans.enterprise.details.oauth-integration'),
            t('plans.enterprise.details.advanced-features'),
            t('plans.enterprise.details.custom-analytics-data-pipelines'),
            t('plans.enterprise.details.custom-features'),
          ]}
        />
      </PlanDetailsContainer>
      <PlanExampleContainer>
        <Fragment key="title">
          {t('plan-select.pricing-example.title')}
        </Fragment>
        <Fragment key="description">
          <Trans
            i18nKey="plans.enterprise.pricing-example.description"
            components={{
              1: <Link href={import.meta.env.VITE_DEMO_FORM_URL} />,
            }}
          />
        </Fragment>
      </PlanExampleContainer>
      <CheckMarkList
        title={t('plan-select.benefits')}
        items={[
          {
            title: t('plans.enterprise.benefits.tailored-solutions.title'),
            description: t(
              'plans.enterprise.benefits.tailored-solutions.description',
            ),
          },
          {
            title: t('plans.enterprise.benefits.premium-support.title'),
            description: t(
              'plans.enterprise.benefits.premium-support.description',
            ),
          },
          {
            title: t('plans.enterprise.benefits.full-feature-access.title'),
            description: t(
              'plans.enterprise.benefits.full-feature-access.description',
            ),
          },
        ]}
      />
    </PlanContainer>
  );
});

export default EnterprisePlan;
